import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import {
  ArrowRightOnRectangleIcon,
  ArrowUpTrayIcon,
  Cog6ToothIcon,
  ExclamationCircleIcon,
  KeyIcon,
  PencilSquareIcon,
  ShieldCheckIcon,
  UserIcon,
} from '@heroicons/react/24/outline';
import {
  badgeClass,
  DetailItem,
  Field,
  getApiErrorMessage,
  inputClass,
  inputErrorClass,
  primaryButtonClass,
  secondaryButtonClass,
} from '../../components/ui/FormControls';
import { isPasswordValid, PasswordChecklist, PasswordInput } from '../../components/users/UserForm';
import TwoFactorSection from '../../components/users/TwoFactorSection';
import userService, { User, UserActivity } from '../../services/user.service';
import { useAuthStore } from '../../store/auth.store';
import { cn } from '../../lib/utils';

const PHONE_PATTERN = /^[0-9+()\-\s]{6,20}$/;
// Pictures travel with every list of users, so they are kept small
const MAX_PICTURE_LENGTH = 40000;
const SIGN_IN_TYPES = ['login', 'failed_login', 'account_locked', 'account_unlocked'];

const cardClass = 'rounded-lg bg-white shadow-sm ring-1 ring-gray-200';

const STATUS_TONES: Record<string, string> = {
  active: 'bg-green-50 text-green-700 ring-green-600/20',
  pending: 'bg-amber-50 text-amber-700 ring-amber-600/20',
  suspended: 'bg-red-50 text-red-700 ring-red-600/20',
  inactive: 'bg-gray-50 text-gray-600 ring-gray-500/20',
};

const titleCase = (value: string) => value.charAt(0).toUpperCase() + value.slice(1).replace(/_/g, ' ');

const formatDateTime = (value?: string | null) =>
  value
    ? new Date(value).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
    : null;

// "Chrome on Windows" from a browser's user-agent text
const describeDevice = (userAgent?: string | null): string => {
  if (!userAgent) return 'Unknown device';
  const browser = /Edg\//.test(userAgent)
    ? 'Edge'
    : /OPR\//.test(userAgent)
      ? 'Opera'
      : /Chrome\//.test(userAgent)
        ? 'Chrome'
        : /Firefox\//.test(userAgent)
          ? 'Firefox'
          : /Safari\//.test(userAgent)
            ? 'Safari'
            : null;
  const system = /Windows/.test(userAgent)
    ? 'Windows'
    : /Android/.test(userAgent)
      ? 'Android'
      : /iPhone|iPad/.test(userAgent)
        ? 'iOS'
        : /Mac OS X/.test(userAgent)
          ? 'macOS'
          : /Linux/.test(userAgent)
            ? 'Linux'
            : null;
  if (!browser && !system) return 'Another application';
  return [browser ?? 'A browser', system].filter(Boolean).join(' on ');
};

// "::1" and "127.0.0.1" are this computer itself
const describeAddress = (ipAddress?: string | null) =>
  !ipAddress ? null : ['::1', '127.0.0.1', '::ffff:127.0.0.1'].includes(ipAddress) ? 'This computer' : ipAddress.replace(/^::ffff:/, '');

// Crops a picked image to a square, scales it down and returns it as a data URL
const readPicture = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    if (!['image/png', 'image/jpeg', 'image/webp'].includes(file.type)) {
      reject(new Error('Choose a PNG, JPEG or WebP image'));
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      reject(new Error('The image is larger than 8 MB'));
      return;
    }
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('The image could not be read'));
    reader.onload = () => {
      const image = new Image();
      image.onerror = () => reject(new Error('The image could not be read'));
      image.onload = () => {
        const side = Math.min(image.width, image.height);
        const canvas = document.createElement('canvas');
        canvas.width = 160;
        canvas.height = 160;
        const context = canvas.getContext('2d');
        if (!context) {
          reject(new Error('The image could not be processed'));
          return;
        }
        context.fillStyle = '#ffffff';
        context.fillRect(0, 0, 160, 160);
        context.drawImage(image, (image.width - side) / 2, (image.height - side) / 2, side, side, 0, 0, 160, 160);
        const result = canvas.toDataURL('image/jpeg', 0.85);
        if (result.length > MAX_PICTURE_LENGTH) reject(new Error('The image is too detailed. Try another one.'));
        else resolve(result);
      };
      image.src = String(reader.result);
    };
    reader.readAsDataURL(file);
  });

type PersonalForm = { firstName: string; middleName: string; lastName: string; phone: string; mobile: string };

const toPersonalForm = (user?: User | null): PersonalForm => ({
  firstName: user?.firstName ?? '',
  middleName: user?.middleName ?? '',
  lastName: user?.lastName ?? '',
  phone: user?.phone ?? '',
  mobile: user?.mobile ?? '',
});

const SectionHeader: React.FC<{
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  title: string;
  description: string;
}> = ({ icon: Icon, title, description }) => (
  <div className="flex items-start gap-3 border-b border-gray-200 px-4 py-3">
    <div className="rounded-lg bg-indigo-50 p-2">
      <Icon className="h-5 w-5 text-indigo-600" />
    </div>
    <div>
      <h2 className="text-sm font-semibold text-gray-900">{title}</h2>
      <p className="mt-0.5 text-xs text-gray-500">{description}</p>
    </div>
  </div>
);

const UserProfile: React.FC = () => {
  const queryClient = useQueryClient();
  const storeUser = useAuthStore((state) => state.user);
  const setStoreUser = useAuthStore((state) => state.setUser);

  const { data: user, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['profile'],
    queryFn: () => userService.getCurrentUserProfile(),
  });
  const { data: signIns = [], isLoading: signInsLoading } = useQuery({
    queryKey: ['profile', 'sign-ins'],
    queryFn: () => userService.getMyActivities(8, SIGN_IN_TYPES),
  });
  const { data: activities = [], isLoading: activitiesLoading } = useQuery({
    queryKey: ['profile', 'activities'],
    queryFn: () => userService.getMyActivities(30),
  });
  // What was done to the account, without the sign-ins listed separately
  const changes = useMemo(() => activities.filter((activity) => !SIGN_IN_TYPES.includes(activity.activityType)).slice(0, 8), [activities]);

  const [form, setForm] = useState<PersonalForm>(toPersonalForm());
  const [errors, setErrors] = useState<Partial<Record<keyof PersonalForm, string>>>({});
  const [pictureBusy, setPictureBusy] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);
  const loadedFor = useRef<string | null>(null);

  // Fill the form when the profile arrives; a later refetch never overwrites what is being typed
  useEffect(() => {
    if (!user || loadedFor.current === user.id) return;
    loadedFor.current = user.id;
    setForm(toPersonalForm(user));
  }, [user]);

  const dirty = !!user && JSON.stringify(form) !== JSON.stringify(toPersonalForm(user));

  // The header shows the name and picture of the signed-in user: keep it in step
  const syncHeader = (saved: User) => {
    const next = {
      ...(storeUser as any),
      firstName: saved.firstName,
      lastName: saved.lastName,
      name: [saved.firstName, saved.lastName].filter(Boolean).join(' '),
      profilePicture: saved.profilePicture ?? null,
    };
    setStoreUser(next);
    try {
      localStorage.setItem('user', JSON.stringify(next));
    } catch {
      // The header still updates for this visit
    }
  };

  const saveProfile = useMutation({
    mutationFn: (input: Partial<PersonalForm> & { profilePicture?: string }) => userService.updateCurrentUserProfile(input),
    onSuccess: (saved) => {
      queryClient.setQueryData(['profile'], saved);
      queryClient.invalidateQueries({ queryKey: ['profile', 'activities'] });
      syncHeader(saved);
    },
  });

  const setField = (name: keyof PersonalForm, value: string) => {
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => {
        const { [name]: _removed, ...rest } = prev;
        return rest;
      });
    }
  };

  const handleSave = (event: React.FormEvent) => {
    event.preventDefault();
    const found: Partial<Record<keyof PersonalForm, string>> = {};
    if (!form.firstName.trim()) found.firstName = 'Enter your first name';
    if (!form.lastName.trim()) found.lastName = 'Enter your last name';
    if (form.phone.trim() && !PHONE_PATTERN.test(form.phone.trim())) found.phone = 'Enter a valid phone number';
    if (form.mobile.trim() && !PHONE_PATTERN.test(form.mobile.trim())) found.mobile = 'Enter a valid mobile number';
    setErrors(found);
    const first = Object.keys(found)[0];
    if (first) {
      document.getElementById(`profile-${first}`)?.focus();
      return;
    }
    saveProfile.mutate(
      {
        firstName: form.firstName.trim(),
        middleName: form.middleName.trim(),
        lastName: form.lastName.trim(),
        phone: form.phone.trim(),
        mobile: form.mobile.trim(),
      },
      {
        onSuccess: (saved) => {
          setForm(toPersonalForm(saved));
          toast.success('Profile saved');
        },
        onError: (saveError) => toast.error(getApiErrorMessage(saveError, 'Your profile could not be saved')),
      },
    );
  };

  const pickPicture = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setPictureBusy(true);
    try {
      const picture = await readPicture(file);
      await saveProfile.mutateAsync({ profilePicture: picture });
      toast.success('Picture updated');
    } catch (pictureError) {
      toast.error(pictureError instanceof Error && !(pictureError as any).response ? pictureError.message : getApiErrorMessage(pictureError, 'The picture could not be saved'));
    } finally {
      setPictureBusy(false);
    }
  };

  const removePicture = () => {
    saveProfile.mutate(
      { profilePicture: '' },
      {
        onSuccess: () => toast.success('Picture removed'),
        onError: (saveError) => toast.error(getApiErrorMessage(saveError, 'The picture could not be removed')),
      },
    );
  };

  // --- password
  const [passwords, setPasswords] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [passwordErrors, setPasswordErrors] = useState<Record<string, string>>({});
  const changePassword = useMutation({
    mutationFn: () => userService.changePassword(passwords),
    onSuccess: () => {
      setPasswords({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setPasswordErrors({});
      queryClient.invalidateQueries({ queryKey: ['profile', 'activities'] });
      toast.success('Password changed. Use the new one the next time you sign in.');
    },
    onError: (changeError) => {
      const message = getApiErrorMessage(changeError, 'The password could not be changed');
      // What the server says about the current password belongs next to that field
      if (/current password/i.test(message)) setPasswordErrors({ currentPassword: message });
      else if (/different/i.test(message)) setPasswordErrors({ newPassword: message });
      else toast.error(message);
    },
  });

  const setPassword = (name: keyof typeof passwords, value: string) => {
    setPasswords((prev) => ({ ...prev, [name]: value }));
    if (passwordErrors[name]) {
      setPasswordErrors((prev) => {
        const { [name]: _removed, ...rest } = prev;
        return rest;
      });
    }
  };

  const handleChangePassword = (event: React.FormEvent) => {
    event.preventDefault();
    const found: Record<string, string> = {};
    if (!passwords.currentPassword) found.currentPassword = 'Enter your current password';
    if (!passwords.newPassword) found.newPassword = 'Enter a new password';
    else if (!isPasswordValid(passwords.newPassword)) found.newPassword = 'The password does not meet the rules below';
    else if (passwords.newPassword === passwords.currentPassword) found.newPassword = 'The new password must be different from the current one';
    if (passwords.confirmPassword !== passwords.newPassword) found.confirmPassword = 'The passwords do not match';
    setPasswordErrors(found);
    if (Object.keys(found).length === 0) changePassword.mutate();
  };

  if (isError) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-semibold text-gray-900">My Profile</h1>
        <div className={cn(cardClass, 'p-10 text-center')}>
          <p className="text-sm text-gray-700">{getApiErrorMessage(error, 'Your profile could not be loaded.')}</p>
          <button type="button" onClick={() => refetch()} className={cn(secondaryButtonClass, 'mt-4')}>
            Try again
          </button>
        </div>
      </div>
    );
  }

  const fullName = user ? [user.firstName, user.middleName, user.lastName].filter(Boolean).join(' ') || user.email : '';
  const initials = user ? ((user.firstName?.[0] ?? '') + (user.lastName?.[0] ?? '')).toUpperCase() || user.email[0].toUpperCase() : '';
  const fieldClass = (name: keyof PersonalForm) => cn(inputClass, errors[name] && inputErrorClass);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">My Profile</h1>
        <p className="mt-1 text-sm text-gray-500">Your own details, password and the recent activity on your account.</p>
      </div>

      {/* Who you are */}
      <div className={cn(cardClass, 'flex flex-col gap-4 p-4 sm:flex-row sm:items-center')}>
        {isLoading || !user ? (
          <div className="h-20 w-full animate-pulse rounded bg-gray-100" />
        ) : (
          <>
            <div className="flex items-center gap-4">
              {user.profilePicture ? (
                <img src={user.profilePicture} alt="Your profile" className="h-20 w-20 shrink-0 rounded-full object-cover ring-1 ring-gray-200" />
              ) : (
                <span className="flex h-20 w-20 shrink-0 items-center justify-center rounded-full bg-indigo-100 text-2xl font-semibold text-indigo-700">
                  {initials}
                </span>
              )}
              <div className="sm:hidden">
                <p className="text-lg font-semibold text-gray-900">{fullName}</p>
                <p className="text-sm text-gray-500">{user.email}</p>
              </div>
            </div>
            <div className="min-w-0 flex-1">
              <div className="hidden sm:block">
                <p className="truncate text-lg font-semibold text-gray-900">{fullName}</p>
                <p className="truncate text-sm text-gray-500">{user.email}</p>
              </div>
              <div className="mt-2 flex flex-wrap items-center gap-1.5">
                <span className={cn(badgeClass, 'bg-indigo-50 text-indigo-700 ring-indigo-600/20')}>
                  {user.role?.displayName || user.role?.name || 'No role'}
                </span>
                <span className={cn(badgeClass, STATUS_TONES[user.status] ?? STATUS_TONES.inactive)}>{titleCase(user.status)}</span>
                <span
                  className={cn(
                    badgeClass,
                    user.emailVerified ? 'bg-green-50 text-green-700 ring-green-600/20' : 'bg-amber-50 text-amber-700 ring-amber-600/20',
                  )}
                >
                  {user.emailVerified ? 'Email verified' : 'Email not verified'}
                </span>
              </div>
            </div>
            <div className="flex shrink-0 flex-wrap items-center gap-2">
              <button type="button" onClick={() => fileInput.current?.click()} disabled={pictureBusy || saveProfile.isPending} className={secondaryButtonClass}>
                <ArrowUpTrayIcon className="-ml-0.5 mr-2 h-4 w-4" />
                {pictureBusy ? 'Saving…' : user.profilePicture ? 'Change picture' : 'Add picture'}
              </button>
              {user.profilePicture && (
                <button type="button" onClick={removePicture} disabled={saveProfile.isPending} className="px-2 text-sm font-medium text-red-600 hover:text-red-500 disabled:opacity-50">
                  Remove
                </button>
              )}
              <input ref={fileInput} type="file" accept="image/png,image/jpeg,image/webp" onChange={pickPicture} className="hidden" aria-label="Profile picture file" />
            </div>
          </>
        )}
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
        <div className="space-y-6 xl:col-span-2">
          {/* Personal details */}
          <form onSubmit={handleSave} noValidate className={cardClass}>
            <SectionHeader icon={UserIcon} title="Personal details" description="Your name and how to reach you." />
            <fieldset disabled={isLoading || saveProfile.isPending} className="grid grid-cols-1 gap-x-4 gap-y-4 p-4 sm:grid-cols-2">
              <Field label="First Name" htmlFor="profile-firstName" required error={errors.firstName}>
                <input id="profile-firstName" type="text" maxLength={50} value={form.firstName} onChange={(e) => setField('firstName', e.target.value)} className={fieldClass('firstName')} />
              </Field>
              <Field label="Last Name" htmlFor="profile-lastName" required error={errors.lastName}>
                <input id="profile-lastName" type="text" maxLength={50} value={form.lastName} onChange={(e) => setField('lastName', e.target.value)} className={fieldClass('lastName')} />
              </Field>
              <Field label="Middle Name" htmlFor="profile-middleName">
                <input id="profile-middleName" type="text" maxLength={50} value={form.middleName} onChange={(e) => setField('middleName', e.target.value)} className={inputClass} />
              </Field>
              <div className="hidden sm:block" />
              <Field label="Phone" htmlFor="profile-phone" error={errors.phone}>
                <input id="profile-phone" type="tel" maxLength={20} value={form.phone} onChange={(e) => setField('phone', e.target.value)} className={fieldClass('phone')} />
              </Field>
              <Field label="Mobile" htmlFor="profile-mobile" error={errors.mobile}>
                <input id="profile-mobile" type="tel" maxLength={20} value={form.mobile} onChange={(e) => setField('mobile', e.target.value)} className={fieldClass('mobile')} />
              </Field>
            </fieldset>
            <div className="flex items-center justify-between gap-3 border-t border-gray-200 px-4 py-3">
              <p className={cn('text-sm', dirty ? 'text-amber-700' : 'text-gray-500')}>{dirty ? 'You have unsaved changes' : 'Everything is saved'}</p>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setForm(toPersonalForm(user));
                    setErrors({});
                  }}
                  disabled={!dirty || saveProfile.isPending}
                  className={secondaryButtonClass}
                >
                  Discard
                </button>
                <button type="submit" disabled={!dirty || saveProfile.isPending} className={primaryButtonClass}>
                  {saveProfile.isPending ? 'Saving…' : 'Save Changes'}
                </button>
              </div>
            </div>
          </form>

          {/* Set by an administrator */}
          <section className={cardClass}>
            <SectionHeader icon={ShieldCheckIcon} title="Account" description="Set by an administrator on the Users page. Ask them if something here is wrong." />
            {isLoading || !user ? (
              <div className="p-4">
                <div className="h-16 animate-pulse rounded bg-gray-100" />
              </div>
            ) : (
              <dl className="grid grid-cols-2 gap-4 p-4 sm:grid-cols-3">
                <DetailItem label="Email">{user.email}</DetailItem>
                <DetailItem label="Username">{user.username || '—'}</DetailItem>
                <DetailItem label="Employee ID">{user.employeeId || '—'}</DetailItem>
                <DetailItem label="Department">{user.department || '—'}</DetailItem>
                <DetailItem label="Designation">{user.designation || '—'}</DetailItem>
                <DetailItem label="User type">{titleCase(user.userType)}</DetailItem>
                <DetailItem label="Member since">{formatDateTime(user.createdAt)}</DetailItem>
                <DetailItem label="Last sign-in">{formatDateTime(user.lastLoginAt) ?? 'Never'}</DetailItem>
              </dl>
            )}
          </section>

          {/* Password */}
          <form onSubmit={handleChangePassword} noValidate className={cardClass}>
            <SectionHeader icon={KeyIcon} title="Password" description="Change the password you sign in with." />
            <fieldset disabled={changePassword.isPending} className="grid grid-cols-1 gap-x-4 gap-y-4 p-4 sm:grid-cols-2">
              <Field label="Current Password" htmlFor="profile-currentPassword" required error={passwordErrors.currentPassword}>
                <PasswordInput
                  id="profile-currentPassword"
                  value={passwords.currentPassword}
                  onChange={(value) => setPassword('currentPassword', value)}
                  invalid={!!passwordErrors.currentPassword}
                  autoComplete="current-password"
                />
              </Field>
              <div className="hidden sm:block" />
              <Field label="New Password" htmlFor="profile-newPassword" required error={passwordErrors.newPassword}>
                <PasswordInput id="profile-newPassword" value={passwords.newPassword} onChange={(value) => setPassword('newPassword', value)} invalid={!!passwordErrors.newPassword} />
              </Field>
              <Field label="Confirm New Password" htmlFor="profile-confirmPassword" required error={passwordErrors.confirmPassword}>
                <PasswordInput
                  id="profile-confirmPassword"
                  value={passwords.confirmPassword}
                  onChange={(value) => setPassword('confirmPassword', value)}
                  invalid={!!passwordErrors.confirmPassword}
                />
              </Field>
              <div className="sm:col-span-2">
                <PasswordChecklist value={passwords.newPassword} />
              </div>
            </fieldset>
            <div className="flex justify-end border-t border-gray-200 px-4 py-3">
              <button
                type="submit"
                disabled={changePassword.isPending || (!passwords.currentPassword && !passwords.newPassword && !passwords.confirmPassword)}
                className={primaryButtonClass}
              >
                {changePassword.isPending ? 'Changing…' : 'Change Password'}
              </button>
            </div>
          </form>

          <TwoFactorSection />
        </div>

        <div className="space-y-6">
          {/* Sign-ins */}
          <section className={cardClass}>
            <SectionHeader icon={ArrowRightOnRectangleIcon} title="Sign-in history" description="If one of these was not you, change your password." />
            {signInsLoading ? (
              <div className="space-y-2 p-4">
                <div className="h-4 animate-pulse rounded bg-gray-100" />
                <div className="h-4 w-2/3 animate-pulse rounded bg-gray-100" />
              </div>
            ) : signIns.length === 0 ? (
              <p className="p-4 text-sm text-gray-500">Sign-ins are listed here from your next sign-in on.</p>
            ) : (
              <ul className="divide-y divide-gray-100">
                {signIns.map((entry: UserActivity) => {
                  const failed = entry.activityType !== 'login' && entry.activityType !== 'account_unlocked';
                  return (
                    <li key={entry.id} className="flex items-start gap-3 px-4 py-2.5">
                      {failed ? (
                        <ExclamationCircleIcon className="mt-0.5 h-4 w-4 shrink-0 text-red-500" />
                      ) : (
                        <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-green-500" />
                      )}
                      <div className="min-w-0 flex-1">
                        <p className={cn('text-sm', failed ? 'font-medium text-red-700' : 'text-gray-900')}>
                          {entry.activityType === 'login' ? describeDevice(entry.userAgent) : entry.description || titleCase(entry.activityType)}
                        </p>
                        <p className="text-xs text-gray-500">
                          {[formatDateTime(entry.createdAt), describeAddress(entry.ipAddress)].filter(Boolean).join(' · ')}
                        </p>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>

          {/* What changed */}
          <section className={cardClass}>
            <SectionHeader icon={PencilSquareIcon} title="Recent activity" description="Changes made to your account." />
            {activitiesLoading ? (
              <div className="space-y-2 p-4">
                <div className="h-4 animate-pulse rounded bg-gray-100" />
                <div className="h-4 w-2/3 animate-pulse rounded bg-gray-100" />
              </div>
            ) : changes.length === 0 ? (
              <p className="p-4 text-sm text-gray-500">Nothing has been changed on your account yet.</p>
            ) : (
              <ul className="divide-y divide-gray-100">
                {changes.map((entry) => (
                  <li key={entry.id} className="px-4 py-2.5">
                    <p className="text-sm text-gray-900">{entry.description || titleCase(entry.activityType)}</p>
                    <p className="text-xs text-gray-500">{formatDateTime(entry.createdAt)}</p>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <Link to="/settings" className={cn(cardClass, 'group flex items-center gap-3 p-4 hover:ring-indigo-300')}>
            <Cog6ToothIcon className="h-5 w-5 shrink-0 text-gray-400 group-hover:text-indigo-600" />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-gray-900 group-hover:text-indigo-700">Preferences</p>
              <p className="text-xs text-gray-500">Notifications, start page and rows per page are under Settings.</p>
            </div>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default UserProfile;
